type OrganisationsEntryScreenProps = { onLogin: () => void };

const settings = [
  "Schools and Multi-Academy Trusts", "Further education colleges",
  "Specialist and SEND settings", "Universities and adult education",
  "Independent training providers", "Charities and community organisations",
];

const outcomes = [
  "Greater engagement", "Increasing independence", "Evidence-based confidence",
  "Sustainable progress", "Improved reflection", "Stronger wellbeing",
];

export default function OrganisationsEntryScreen({ onLogin }: OrganisationsEntryScreenProps) {
  return (
    <main className="public-home organisation-page">
      <header className="public-header">
        <a className="public-brand" href="/" aria-label="Calm But Capable home">
          <img src="/logo-white.png" alt="" className="public-logo" />
          <span>Calm But Capable</span>
        </a>
        <div className="organisation-header-actions">
          <a className="organisation-back-link" href="/">Home</a>
          <button className="public-login" type="button" onClick={onLogin}>Login</button>
        </div>
      </header>

      <section className="organisation-hero">
        <div className="organisation-hero-copy">
          <p className="public-kicker">Organisations and Education</p>
          <h1>Help people recognise their capability and move forward.</h1>
          <p className="organisation-lead">
            Calm But Capable helps schools, colleges and organisations understand
            what may be preventing someone from engaging or progressing—and
            identify an achievable next step.
          </p>
          <div className="organisation-hero-actions">
            <a className="organisation-button organisation-button--primary" href="#how-it-works">Explore how CBC works</a>
            <a
              className="organisation-button organisation-button--secondary"
              href="mailto:hello@calmbutcapable.com?subject=Introductory%20conversation%20about%20Calm%20But%20Capable"
            >
              Arrange an introductory conversation
            </a>
          </div>
        </div>
        <aside className="organisation-hero-note">
          <img
            className="organisation-hero-image"
            src="/organisation-support.webp"
            alt="A tutor and student working through a task together"
          />
          <div className="organisation-hero-note-copy">
            <p className="public-card-label">Our starting point</p>
            <blockquote>“Built around the person—not the label.”</blockquote>
            <p>We do not tell people what their “Best Me” should be. We help them understand what matters, recognise their capability and discover a way forward that works for them.</p>
          </div>
        </aside>
      </section>

      <section className="organisation-section organisation-recognition">
        <div className="organisation-section-heading">
          <p className="public-kicker">Look beyond what is visible</p>
          <h2>When capability is hidden by what is getting in the way</h2>
          <p>A student may appear unmotivated. A member of staff may seem disengaged. Someone may understand what needs to be done but still struggle to begin. What we can see does not always explain what is happening.</p>
        </div>
        <div className="organisation-signal-grid">
          <article><span>01</span><h3>They know, but cannot begin</h3><p>The task is understood, yet the first step still feels out of reach.</p></article>
          <article><span>02</span><h3>They appear disengaged</h3><p>Low participation may conceal overload, uncertainty or lost confidence.</p></article>
          <article><span>03</span><h3>Pressure is not helping</h3><p>More urgency can increase resistance, stress or the fear of getting it wrong.</p></article>
          <article><span>04</span><h3>Existing advice has not fitted</h3><p>A capable person may need a different route—not another generic instruction.</p></article>
        </div>
      </section>

      <section className="organisation-section organisation-process" id="how-it-works">
        <div className="organisation-section-heading organisation-section-heading--center">
          <p className="public-kicker">The CBC approach</p>
          <h2>Understanding before action</h2>
          <p>Progress becomes more sustainable when the next action is based on the person’s actual situation, not an assumption about what they should be able to do.</p>
        </div>
        <ol className="organisation-process-grid">
          <li><span>1</span><h3>Understand the barrier</h3><p>Explore what is happening before deciding what needs to change.</p></li>
          <li><span>2</span><h3>Choose an achievable step</h3><p>Reduce the task until forward movement is realistic.</p></li>
          <li><span>3</span><h3>Create evidence of capability</h3><p>Let progress provide credible evidence that the person can move forward.</p></li>
          <li><span>4</span><h3>Reflect and continue</h3><p>Use experience to choose the next step and build greater independence.</p></li>
        </ol>
      </section>

      <section className="organisation-section organisation-audiences">
        <div className="organisation-section-heading">
          <p className="public-kicker">Education first</p>
          <h2>Flexible enough to complement different settings</h2>
          <p>CBC’s initial focus is education. The approach is designed to fit alongside existing teaching, pastoral support and professional practice—not replace them.</p>
        </div>
        <div className="organisation-tag-grid">{settings.map((setting) => <div key={setting}>{setting}</div>)}</div>
      </section>

      <section className="organisation-section">
        <div className="organisation-section-heading organisation-section-heading--center">
          <p className="public-kicker">One philosophy, several forms of support</p>
          <h2>How Calm But Capable can be delivered</h2>
        </div>
        <div className="organisation-delivery-grid">
          <article><p className="public-card-label">Platform</p><h3>Sanctuary</h3><p>Structured guidance, reflection and continuing support that can extend beyond a single lesson or conversation.</p></article>
          <article><p className="public-card-label">Learning</p><h3>Workshops and resources</h3><p>Practical sessions, books, videos and materials that make the CBC approach understandable and usable.</p></article>
          <article><p className="public-card-label">Implementation</p><h3>Onboarding and guidance</h3><p>Support for staff to understand the approach, introduce it thoughtfully and use it alongside existing provision.</p></article>
          <article><p className="public-card-label">Development</p><h3>Pilots and partnerships</h3><p>Structured opportunities to test CBC in real settings, gather feedback and develop credible evidence together.</p></article>
        </div>
      </section>

      <section className="organisation-section organisation-outcomes">
        <div className="organisation-section-heading">
          <p className="public-kicker">What progress may support</p>
          <h2>Outcomes that matter beyond completing one task</h2>
          <p>CBC is designed to help people understand themselves, experience achievable progress and become less dependent on someone else continually telling them what to do next.</p>
        </div>
        <div className="organisation-outcome-list">{outcomes.map((outcome) => <span key={outcome}>{outcome}</span>)}</div>
      </section>

      <section className="organisation-section organisation-proof">
        <div><p className="public-kicker">Developed honestly</p><h2>A functioning platform, with evidence still being built</h2></div>
        <div>
          <p>Sanctuary is functioning and has been tested with real users. Calm But Capable is now developing structured pilots, demonstrations and partnerships to learn what works in different settings and build further evidence.</p>
          <p>We will not promise guaranteed transformation or present early findings as more than they are. CBC itself follows the same principle it teaches: act, reflect, learn and improve.</p>
        </div>
      </section>

      <section className="organisation-section organisation-founder">
        <div className="organisation-founder-mark" aria-hidden="true">NC</div>
        <div>
          <p className="public-kicker">Founded by Nicholas Castle</p>
          <h2>Built from experience of people—not assumptions about them</h2>
          <p>Nicholas has worked across education, investigation, youth work, mentoring and support. Calm But Capable brings together those experiences with a simple belief: people are often more capable than they appear—and more capable than they realise.</p>
        </div>
      </section>

      <section className="organisation-conversation" id="conversation">
        <p className="public-kicker">Begin with a conversation</p>
        <h2>Could Calm But Capable support the people in your organisation?</h2>
        <p>Tell us about your setting, the people you support and what you would like to improve. We will explore whether CBC is a suitable next step.</p>
        <a
          className="organisation-button organisation-button--primary"
          href="mailto:hello@calmbutcapable.com?subject=Introductory%20conversation%20about%20Calm%20But%20Capable"
        >
          Arrange an introductory conversation
        </a>
      </section>

      <footer className="organisation-footer">
        <a className="public-brand" href="/"><img src="/logo-white.png" alt="" className="public-logo" /><span>Calm But Capable</span></a>
        <p>Understanding before action. Capability before limitation.</p>
      </footer>
    </main>
  );
}
